import React, { useEffect, useState } from 'react';
import styles from '../insertsPages.module.css';
import Tabs from '../../../Tabs';
import { FileInput } from '../../../../../../personalisation/src/components/FileInput';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserImageArea, VisualiserTextBox } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct, findVariantForProduct } from '../../../../utilities/visualiserVariants.js';

const tabConfig = [
  { id: 'FRONT', label: 'Front' },
  { id: 'BACK', label: 'Back' }
];

/**
 * InsertPhotoUpload component allows users to upload a photo and add text for their box insert.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function InsertPhotoUpload({ ...rest }) {
  const {
    getImageInput,
    setImageInput,
    getTextInput,
    setTextInput,
    getTransformation,
    setTransformation,
    setVisualiserSelection,
    clearVisualiserSelection
  } = useUserInput();
  const [activeIndex, setActiveIndex] = useState(0);

  const frontImageKey = 'insertPhotoUpload.frontImage';
  const frontImage = getImageInput(frontImageKey);
  const frontTransformation = getTransformation(frontImageKey);

  useEffect(() => {
    const productKey = 'inserts';
    const product = getInsertsAndCardsProduct({ productKey });

    // Match the known "Image Upload" insert variant.
    const variantTitle = 'Image Upload';
    const variant = findVariantForProduct({ product, variantTitle, fallbackOption1: 'Image Upload' });

    setVisualiserSelection({
      productKey,
      variant,
      config: {
        templateId: 'INSERT_CUSTOM_TEXT_IMAGE',
        mapping: {
          text: {
            'insertPhotoUpload.front': 'UVFrontText',
            'insertPhotoUpload.back': 'UVBackText'
          },
          images: {
            'insertPhotoUpload.frontImage': 'UVFrontImage'
          }
        }
      }
    });

    return () => {
      clearVisualiserSelection();
    };
  }, [setVisualiserSelection, clearVisualiserSelection]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs tabs={tabConfig} activeIndex={activeIndex} onChange={setActiveIndex} fullWidth={true} />
        {activeIndex === 0 && (
          <Visualiser type="INSERT" background="var(--color-white)" gap="1rem" subtitle="Upload an image below">
            <VisualiserImageArea
              value={frontImage}
              size={{
                width: '250px',
                height: '220px'
              }}
              position={{
                top: '20px',
                left: '50%'
              }}
              borderRadius="0"
              backgroundColor="var(--color-white)"
              style={{
                border: '0.1rem solid rgba(0, 0, 0, 0.3)',
                transform: 'translateX(-50%)'
              }}
              alt="Box insert photo upload preview"
              scale={frontTransformation.scale}
              x={frontTransformation.x}
              y={frontTransformation.y}
              onUpdate={(updates) => setTransformation(frontImageKey, updates)}
            />
            <VisualiserTextBox
              size={{
                width: '95%',
                minWidth: '250px',
                maxWidth: '280px',
                minHeight: '25px',
                maxHeight: '50px'
              }}
              position={{
                top: '250px',
                left: '50%'
              }}
              font="Amalfi Coast"
              fontSize={32}
              lineHeight={1.7}
              fontWeight="600"
              maxLength={24}
              maxLines={1}
              style={{
                transform: 'translateX(-50%)'
              }}
              value={getTextInput('insertPhotoUpload.front')}
              setValue={(value) => setTextInput('insertPhotoUpload.front', value)}
            />
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <Visualiser
            type="INSERT"
            background={`url(${window?.giftWrapData?.insertsAndCards?.images?.insertBack})`}
            subtitle="Tap to edit"
          >
            <VisualiserTextBox
              size={{
                width: '280px',
                minWidth: '200px',
                maxWidth: '280px',
                minHeight: '50px',
                maxHeight: '200px'
              }}
              position={{
                top: '50%',
                left: '50%'
              }}
              fontWeight="bold"
              maxLength={128}
              maxLines={8}
              lineHeight={1.2}
              fontSize={21}
              style={{
                transform: 'translate(-50%, -50%)'
              }}
              value={getTextInput('insertPhotoUpload.back')}
              setValue={(value) => setTextInput('insertPhotoUpload.back', value)}
            />
          </Visualiser>
        )}

        {activeIndex === 0 && (
          <div className={styles['file-input-container']}>
            <FileInput
              label="Upload a Photo"
              onFileSelect={(value) => setImageInput(frontImageKey, value)}
              accept={'png,jpg,jpeg,webp'}
              disabled={false}
              buttonVariant="primary"
              buttonColor="orange"
              buttonSize="small"
              maxFileSize={5 * 1024 * 1024}
              id="front-image"
              selectedFile={
                frontImage && frontImage.userImage && frontImage.userImage.file instanceof File
                ? frontImage.userImage.file
                : null
              }
              />
          </div>
        )}
      </div>
    </div>
  );
}
